import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { FileChartColumn, LayoutDashboard, CircuitBoard, Radio, FilePenLine, ReceiptText, Landmark, HardHat, MapPin, Cable, Milestone, Sun } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import Breadcrumbs from "@/components/Breadcrumbs";
import StatusBadge from "@/components/StatusBadge";
import OverviewTab from "@/pages/tabs/OverviewTab";
import ElectricalTab from "@/pages/tabs/ElectricalTab";
import NbnTab from "@/pages/tabs/NbnTab";
import VariationsTab from "@/pages/tabs/VariationsTab";
import ClaimsTab from "@/pages/tabs/ClaimsTab";
import FinancialsTab from "@/pages/tabs/FinancialsTab";
import OhsTab from "@/pages/tabs/OhsTab";
import SolarTab from "@/pages/tabs/SolarTab";
import DocPreview from "@/components/DocPreview";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "electrical", label: "Electrical Roadmap", icon: CircuitBoard },
  { key: "nbn", label: "NBN Roadmap", icon: Radio },
  { key: "solar", label: "Solar Roadmap", icon: Sun },
  { key: "variations", label: "Variations", icon: FilePenLine },
  { key: "claims", label: "Claims", icon: ReceiptText },
  { key: "financials", label: "Financials", icon: Landmark },
  { key: "ohs", label: "OHS", icon: HardHat },
];

export default function ProjectPage() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const [project, setProject] = useState(null);
  const [docs, setDocs] = useState([]);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [rmBusy, setRmBusy] = useState(false);
  const [reportPreview, setReportPreview] = useState(null);

  const refresh = useCallback(() => api.get(`/projects/${id}`).then((r) => setProject(r.data)), [id]);
  const refreshDocs = useCallback(() => api.get(`/projects/${id}/documents`).then((r) => setDocs(r.data)), [id]);

  useEffect(() => { refresh(); refreshDocs(); }, [refresh, refreshDocs]);

  const openReport = async (url, filename, setBusy) => {
    setBusy(true);
    try {
      const res = await api.get(url, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      setReportPreview({ filename, blobUrl, content_type: "application/pdf" });
      refreshDocs();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const generatePdf = () =>
    openReport(`/projects/${id}/report/pdf`, `Complete-Project-Report-${project.project_type}-${project.project_number}.pdf`, setPdfBusy);

  const generateRoadmapPdf = () => {
    const street = (project.address.street || "Project").replace(/ /g, "-");
    return openReport(`/projects/${id}/report/electrical`, `BDX_${project.project_number}_${street}_Electrical-Roadmap.pdf`, setRmBusy);
  };

  const closeReportPreview = () => {
    if (reportPreview?.blobUrl) URL.revokeObjectURL(reportPreview.blobUrl);
    setReportPreview(null);
  };

  if (!project) return <div className="text-sm text-gray-500">Loading project…</div>;

  const moduleLabel = project.project_type === "SSH" ? "SSH Modules" : "MMC Modules";
  const modulePath = project.project_type === "SSH" ? "/ssh" : "/mmc";
  const tabLabel = TABS.find((t) => t.key === tab)?.label;

  const common = { project, docs, refresh, refreshDocs };

  return (
    <div data-testid="project-page" className="bdx-page-enter">
      <Breadcrumbs items={[
        { label: "Dashboard", to: "/" },
        { label: moduleLabel, to: modulePath },
        { label: `Project ${project.project_number}`, to: `/projects/${id}` },
        { label: tabLabel },
      ]} />

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0_3px_12px_rgba(16,19,21,0.055)]">
        <div className="h-1 bg-[#765297]" />
        <div className="flex flex-col items-start justify-between gap-6 px-6 py-5 lg:flex-row lg:px-7">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-md border border-[#d9cde5] bg-[#f3eff7] px-2.5 py-1.5 font-mono-data text-xs font-semibold text-[#604184]" data-testid="project-number-chip">{project.display_reference || `${project.project_type} ${project.project_number}`}</span>
            <StatusBadge status={project.status} testId="project-status-badge" />
            {project.pit_overdue && <StatusBadge status="Pit Payment Overdue" tone="red" />}
          </div>
          <h1 className="mt-3 flex items-start gap-2 font-heading text-2xl font-bold leading-tight text-gray-950 sm:text-3xl">
            <MapPin size={20} strokeWidth={1.8} className="mt-1 shrink-0 text-[#765297]" /> {project.label}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-gray-600">
            <span className="inline-flex items-center gap-2"><Cable size={14} strokeWidth={1.5} className="text-gray-400" /><span className="bdx-label">Distributor</span> <span className="font-semibold text-gray-800">{project.distributor}</span></span>
            <span><span className="bdx-label">Supply</span> <span className="ml-1 font-semibold text-gray-800">{project.supply_type}</span></span>
            <span data-testid="dwellings-summary" className="font-semibold text-gray-950">
              {project.dwellings_connected} of {project.dwellings_required ?? project.dwellings_total} Dwellings Connected
              {(project.dwellings_waived || 0) > 0 && <span data-testid="dwellings-summary-waived" className="ml-2 font-medium text-sky-700">· {project.dwellings_waived} by others</span>}
            </span>
          </div>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row lg:flex-col">
          <button data-testid="generate-pdf-btn" onClick={generatePdf} disabled={pdfBusy} className="bdx-btn-secondary justify-center">
            <FileChartColumn size={16} strokeWidth={1.7} className="text-[#604184]" /> {pdfBusy ? "Generating…" : "Complete Project Report"}
          </button>
          <button data-testid="generate-roadmap-pdf-btn" onClick={generateRoadmapPdf} disabled={rmBusy} className="bdx-btn-secondary justify-center">
            <Milestone size={16} strokeWidth={1.7} className="text-[#604184]" /> {rmBusy ? "Generating…" : "Electrical Roadmap Report"}
          </button>
        </div>
        </div>
      </div>

      <div className="mt-5 flex gap-1 overflow-x-auto border-b border-gray-300" role="tablist" aria-label="Project sections">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
          <button
            key={t.key}
            data-testid={`project-tab-${t.key}`}
            onClick={() => setParams({ tab: t.key })}
            role="tab"
            aria-selected={tab === t.key}
            className={`-mb-px flex items-center gap-2 whitespace-nowrap border-b-2 px-3.5 py-3 text-sm transition-[color,border-color,background-color] duration-150 ${
              tab === t.key ? "border-[#765297] bg-[#f6f2f8] font-semibold text-[#604184]" : "border-transparent text-gray-500 hover:border-[#d9cde5] hover:bg-white/60 hover:text-gray-900"
            }`}
          >
            <Icon size={16} strokeWidth={tab === t.key ? 1.9 : 1.6} />
            {t.label}
          </button>
          );
        })}
      </div>

      <div className="mt-6">
        {tab === "overview" && <OverviewTab {...common} />}
        {tab === "electrical" && <ElectricalTab {...common} />}
        {tab === "nbn" && <NbnTab {...common} />}
        {tab === "solar" && <SolarTab {...common} />}
        {tab === "variations" && <VariationsTab {...common} />}
        {tab === "claims" && <ClaimsTab {...common} />}
        {tab === "financials" && <FinancialsTab {...common} />}
        {tab === "ohs" && <OhsTab {...common} />}
      </div>

      {reportPreview && <DocPreview doc={reportPreview} onClose={closeReportPreview} />}
    </div>
  );
}
